import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ConfirmDialog from "../ui/ConfirmDialog.js";
import { navigateToTop } from "../../utils/Constants.js";
import { downloadFile } from "../../utils/download.js";
import { API_BASE } from "../../config/apiBase";
import "../../styles/components/interview-tabs/_user-recording.scss";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(value) {
    if (!value) return "";
    const d =
        typeof value === "string" || typeof value === "number"
            ? new Date(value)
            : value;
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString([], {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

function formatTime(value) {
    if (!value) return "";
    const d =
        typeof value === "string" || typeof value === "number"
            ? new Date(value)
            : value;
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDuration(ms) {
    if (!ms) return null;
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

function recCodeFromFilename(filename) {
    if (!filename) return null;
    const match = String(filename).match(/-(\d+)\.(\w+)$/);
    if (!match) return null;
    return `REC-${match[1].slice(-6)}`;
}

function humanizeRecordingName(name) {
    if (!name) return "";
    if (name.includes(" ")) return name;
    const withoutExt = String(name).replace(/\.[^/.]+$/, "");
    const withoutSuffix = withoutExt.replace(/-\d+$/, "");
    const words = withoutSuffix.split(/[-_]+/).filter(Boolean);
    if (!words.length) return withoutExt;
    const sentence = words.join(" ");
    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconTrash = () => (
    <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M2 4h11M5 4V2.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 .5.5V4M6 7v4M9 7v4M3 4l.8 8.5a.5.5 0 0 0 .5.5h6.4a.5.5 0 0 0 .5-.5L12 4"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconDownload = () => (
    <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M7.5 2v8M4 7l3.5 3.5L11 7M2 13h11"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconPlay = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
    >
        <circle
            cx="10"
            cy="10"
            r="9"
            fill="rgba(255,255,255,0.15)"
            stroke="rgba(255,255,255,0.5)"
            strokeWidth="1"
        />
        <path d="M8 7l6 3-6 3V7z" fill="#fff" />
    </svg>
);

const IconClock = () => (
    <svg
        width="11"
        height="11"
        viewBox="0 0 11 11"
        fill="none"
        aria-hidden="true"
    >
        <circle
            cx="5.5"
            cy="5.5"
            r="4.5"
            stroke="currentColor"
            strokeWidth="1.1"
        />
        <path
            d="M5.5 3v2.5l1.5 1.5"
            stroke="currentColor"
            strokeWidth="1.1"
            strokeLinecap="round"
        />
    </svg>
);

const IconCalendar = () => (
    <svg
        width="11"
        height="11"
        viewBox="0 0 11 11"
        fill="none"
        aria-hidden="true"
    >
        <rect
            x="1"
            y="2"
            width="9"
            height="8"
            rx="1"
            stroke="currentColor"
            strokeWidth="1.1"
        />
        <path
            d="M1 5h9M4 1v2M7 1v2"
            stroke="currentColor"
            strokeWidth="1.1"
            strokeLinecap="round"
        />
    </svg>
);

const IconFilm = () => (
    <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden="true"
    >
        <rect
            x="4"
            y="8"
            width="24"
            height="16"
            rx="2"
            stroke="currentColor"
            strokeWidth="1.5"
        />
        <path
            d="M4 12h4M4 16h4M4 20h4M24 12h4M24 16h4M24 20h4M12 8v16M20 8v16"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
);

// ─── Recording card ───────────────────────────────────────────────────────────
function RecordingCard({ recording, to, onOpen, onDownload, onDelete, index }) {
    const [thumbHovered, setThumbHovered] = useState(false);

    const title =
        recording.question ||
        recording.question_title ||
        recording.questionTitle ||
        recording.questionText ||
        humanizeRecordingName(recording.name) ||
        "Recording";
    const createdAt = recording.created_at || recording.date;
    const date = formatDate(createdAt);
    const time = formatTime(createdAt);
    const duration = formatDuration(recording.duration_ms);
    const recLabel = recCodeFromFilename(recording.filename || recording.name);
    const previewSrc = recording.preview_url || recording.url;
    const isPending =
        recording.transcript_status === "pending" ||
        recording.feedback_status === "pending";
    const isLink = Boolean(to);

    const thumbContent = (
        <>
            {previewSrc ? (
                <video
                    className="rc__video"
                    src={previewSrc}
                    muted
                    playsInline
                    preload="metadata"
                    aria-hidden="true"
                />
            ) : (
                <div className="rc__video-placeholder" aria-hidden="true">
                    <IconFilm />
                </div>
            )}

            {isPending ? (
                <div className="rc__status-overlay" aria-hidden="true">
                    <span className="rc__spinner" />
                    <span className="rc__status-label">Processing…</span>
                </div>
            ) : (
                <div
                    className={`rc__play-overlay${thumbHovered ? " is-visible" : ""}`}
                    aria-hidden="true"
                >
                    <IconPlay />
                </div>
            )}
        </>
    );

    const metaContent = (
        <div className="rc__meta">
            {date && (
                <span className="rc__meta-item">
                    <IconCalendar /> {date}
                    {time && (
                        <span className="rc__meta-sep" aria-hidden="true" />
                    )}
                    {time}
                </span>
            )}
            {duration && (
                <span className="rc__meta-item">
                    <IconClock /> {duration}
                </span>
            )}
        </div>
    );

    return (
        <div
            className={`rc${isPending ? " rc--pending" : ""}`}
            role="listitem"
            style={{ "--card-index": index }}
        >
            {/* Thumbnail */}
            <div
                className="rc__thumb"
                onMouseEnter={() => setThumbHovered(true)}
                onMouseLeave={() => setThumbHovered(false)}
            >
                {isLink ? (
                    <Link
                        to={to}
                        className="rc__thumb-link"
                        aria-disabled={isPending}
                        aria-label={`View recording: ${title}`}
                        onClick={(e) => {
                            if (isPending) e.preventDefault();
                        }}
                    >
                        {thumbContent}
                    </Link>
                ) : (
                    <button
                        type="button"
                        className="rc__thumb-link"
                        onClick={() => {
                            if (!isPending) {
                                onOpen?.(recording.url);
                                navigateToTop();
                            }
                        }}
                        aria-label={`Play recording: ${title}`}
                        aria-disabled={isPending}
                        disabled={isPending}
                    >
                        {thumbContent}
                    </button>
                )}
            </div>

            {/* Info */}
            <div className="rc__info">
                <div className="rc__info-top">
                    {recLabel && <span className="rc__badge">{recLabel}</span>}
                    {isPending && (
                        <span className="rc__badge rc__badge--pending">
                            Processing
                        </span>
                    )}
                </div>
                <p className="rc__title">{title}</p>
                {metaContent}
            </div>

            {/* Actions */}
            <div className="rc__actions" aria-label="Recording actions">
                <button
                    type="button"
                    className="rc__action-btn rc__action-btn--danger"
                    aria-label="Delete recording"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDelete(recording);
                    }}
                >
                    <IconTrash />
                </button>
                <button
                    type="button"
                    className="rc__action-btn"
                    aria-label="Download recording"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void onDownload(recording);
                    }}
                >
                    <IconDownload />
                </button>
            </div>
        </div>
    );
}

// ─── Skeleton card ────────────────────────────────────────────────────────────
function SkeletonCard({ index }) {
    return (
        <div
            className="rc rc--skeleton"
            role="listitem"
            aria-hidden="true"
            style={{ "--card-index": index }}
        >
            <div className="rc__thumb">
                <div className="sk sk--thumb" />
            </div>
            <div className="rc__info">
                <div className="sk sk--badge" />
                <div className="sk sk--title" />
                <div className="sk sk--meta" />
            </div>
            <div className="rc__actions">
                <div className="sk sk--btn" />
                <div className="sk sk--btn" />
            </div>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function UserRecording({
    recordings = [],
    loading = false,
    questionSlug,
    handlePlayback,
    handleDelete,
    downloadToken,
}) {
    const [pendingDelete, setPendingDelete] = useState(null);

    const handleDownload = async (recording) => {
        if (!recording) return;
        const id = recording.id ?? recording.recording_id;
        const baseName = (recording.name || recording.filename || "recording")
            .replace(/\.\w+$/, "");
        if (id && downloadToken) {
            const filename = `${baseName}.mp4`;
            const url = `${API_BASE}/me/recordings/${id}/download?format=mp4`;
            await downloadFile({ url, filename, token: downloadToken });
            return;
        }
        if (recording.url) {
            const fallbackExt = recording.url.split(".").pop() || "webm";
            const filename = `${baseName}.${fallbackExt}`;
            await downloadFile({
                url: recording.url,
                filename,
                token: downloadToken,
            });
        }
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        const id =
            pendingDelete.id ??
            pendingDelete.recording_id ??
            pendingDelete._id ??
            pendingDelete;
        await handleDelete?.(id);
        setPendingDelete(null);
    };

    const count = recordings.length;

    return (
        <div className="ur">
            {/* Header */}
            <div className="ur__header">
                <div className="ur__header-left">
                    <p className="ur__hint">
                        Click a recording to view playback and feedback.
                    </p>
                </div>
                {!loading && (
                    <span className="ur__count" aria-live="polite">
                        {count} {count === 1 ? "recording" : "recordings"}
                    </span>
                )}
            </div>

            {/* List */}
            <div className="ur__list" role="list">
                {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                        <SkeletonCard key={i} index={i} />
                    ))
                ) : count > 0 ? (
                    recordings.map((rec, i) => {
                        const id = rec.id ?? rec.recording_id;
                        const to =
                            questionSlug && id
                                ? `/dashboard/questions/${questionSlug}/recording/${id}`
                                : null;
                        return (
                            <RecordingCard
                                key={id || rec.url}
                                recording={rec}
                                to={to}
                                onOpen={handlePlayback}
                                onDownload={handleDownload}
                                onDelete={setPendingDelete}
                                index={i}
                            />
                        );
                    })
                ) : (
                    <div className="ur__empty" role="status">
                        <div className="ur__empty-icon" aria-hidden="true">
                            <IconFilm />
                        </div>
                        <p className="ur__empty-title">No recordings yet</p>
                        <p className="ur__empty-body">
                            Hit "Practice now" to record your first answer. It
                            will appear here when ready.
                        </p>
                    </div>
                )}
            </div>

            <ConfirmDialog
                open={!!pendingDelete}
                title="Delete this recording?"
                description="This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                tone="danger"
                onCancel={() => setPendingDelete(null)}
                onConfirm={confirmDelete}
            />
        </div>
    );
}
